import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AuthCallback handles the Google OAuth callback.
 * It extracts session_id from URL fragment and exchanges it for user data.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Session invalide');
          navigate('/login');
          return;
        }

        // Exchange session_id for user data
        const user = await processGoogleSession(sessionId);
        
        toast.success('Connexion Google reussie!');

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        // Redirect based on role
        switch (user.role) {
          case 'patient':
            navigate('/dashboard', { replace: true });
            break;
          case 'professional':
            navigate('/pro/dashboard', { replace: true });
            break;
          case 'agent':
            navigate('/agent/dashboard', { replace: true });
            break;
          case 'admin':
            navigate('/admin/dashboard', { replace: true });
            break;
          default:
            navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('Google auth callback error:', error);
        const errorMsg = error.response?.data?.detail || 'Erreur de connexion Google';
        toast.error(errorMsg);
        navigate('/login');
      }
    };

    handleCallback();
  }, [navigate, processGoogleSession]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-blue-50 flex flex-col">
      <header className="p-4 md:p-6">
        <Logo />
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="border-0 shadow-xl max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-red-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Connexion en cours...
            </h2>
            <p className="text-slate-600">
              Veuillez patienter pendant que nous finalisons votre connexion Google.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AuthCallback;
